/**
 * AdminLmxPanel — configuración de Lumière (LMX) sin redeploy:
 * una imagen por pieza del personaje, encendido por defecto, tope diario de
 * turnos y el consumo de tokens por usuario.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Loader2, RefreshCw, Save, Upload, Sparkles } from 'lucide-react';
import { toast } from 'sonner';
import { adminService } from '../../services/adminService';
import { ASSET_LABELS, DEFAULT_ASSETS } from '../../lmx/assets';

const SLOTS = Object.keys(ASSET_LABELS);

export default function AdminLmxPanel() {
  const [cfg, setCfg] = useState(null);
  const [usage, setUsage] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const fileRefs = useRef({});

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([adminService.getLmxConfig(), adminService.getLmxUsage()])
      .then(([c, u]) => { setCfg(c.config); setUsage(u.users || []); })
      .catch(() => toast.error('No se pudo cargar la configuración de LMX'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const setAsset = (slot, value) =>
    setCfg((c) => ({ ...c, assets: { ...(c.assets || {}), [slot]: value || null } }));

  const upload = async (slot, file) => {
    if (!file) return;
    if (file.size > 8 * 1024 * 1024) { toast.error('La imagen supera los 8 MB'); return; }
    setBusy(true);
    try {
      const { url } = await adminService.uploadLmxAsset(file);
      setAsset(slot, url);
      toast.success('Imagen subida. Acordate de publicar.');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo subir la imagen');
    } finally { setBusy(false); }
  };

  const publish = async () => {
    setBusy(true);
    try {
      const res = await adminService.putLmxConfig({
        assets: cfg.assets || {},
        default_on: !!cfg.default_on,
        daily_turn_limit: Number(cfg.daily_turn_limit) || 0,
        credit_factor: Math.min(5, Math.max(1, Number(cfg.credit_factor) || 1.5)),
        note: 'Publicado desde Administración',
      });
      setCfg(res.config);
      toast.success('Configuración de LMX publicada');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'No se pudo publicar');
    } finally { setBusy(false); }
  };

  if (loading) {
    return (
      <div className="admin-tab-body" data-testid="admin-lmx-loading">
        <Loader2 className="spin" size={16} /> Cargando…
      </div>
    );
  }
  if (!cfg) return <div className="admin-tab-body">Sin datos.</div>;

  return (
    <div className="admin-tab-body" data-testid="admin-lmx-panel">
      <div className="admin-section-head">
        <h3><Sparkles size={14} /> Lumière (LMX)</h3>
        <button className="admin-btn" onClick={load} data-testid="admin-lmx-reload">
          <RefreshCw size={13} /> Actualizar
        </button>
      </div>

      <p className="admin-hint">
        Cada pieza del personaje puede tener su propia imagen. Si dejás un slot
        vacío, se usa la que viene con la app.
      </p>

      <div className="admin-lmx-slots">
        {SLOTS.map((slot) => (
          <div className="admin-lmx-slot" key={slot} data-testid={`admin-lmx-slot-${slot}`}>
            <div className="admin-lmx-slot-prev">
              <img src={cfg.assets?.[slot] || DEFAULT_ASSETS[slot]} alt={ASSET_LABELS[slot]} />
            </div>
            <div className="admin-lmx-slot-body">
              <strong>{ASSET_LABELS[slot]}</strong>
              <input
                type="text"
                placeholder="URL de la imagen (vacío = la de la app)"
                value={cfg.assets?.[slot] || ''}
                onChange={(e) => setAsset(slot, e.target.value)}
                data-testid={`admin-lmx-url-${slot}`}
              />
              <div className="admin-lmx-slot-actions">
                <button
                  className="admin-btn"
                  disabled={busy}
                  onClick={() => fileRefs.current[slot]?.click()}
                  data-testid={`admin-lmx-upload-${slot}`}
                >
                  <Upload size={12} /> Subir
                </button>
                {cfg.assets?.[slot] && (
                  <button className="admin-btn" onClick={() => setAsset(slot, null)}
                          data-testid={`admin-lmx-clear-${slot}`}>
                    Volver a la original
                  </button>
                )}
              </div>
              <input
                type="file"
                accept="image/*"
                hidden
                ref={(el) => { fileRefs.current[slot] = el; }}
                onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ''; upload(slot, f); }}
              />
            </div>
          </div>
        ))}
      </div>

      <div className="admin-lmx-flags">
        <label>
          <input
            type="checkbox"
            checked={!!cfg.default_on}
            onChange={(e) => setCfg({ ...cfg, default_on: e.target.checked })}
            data-testid="admin-lmx-default-on"
          />
          Encender LMX por defecto (para usuarios sin preferencia propia)
        </label>
        <label>
          Tope de pedidos por usuario y por día (0 = sin tope)
          <input
            type="number"
            min="0"
            value={cfg.daily_turn_limit ?? 0}
            onChange={(e) => setCfg({ ...cfg, daily_turn_limit: e.target.value })}
            data-testid="admin-lmx-daily-limit"
          />
        </label>
        <label>
          Factor de cobro de LMX (costo real × factor → créditos; 1 a 5, sin ganancia = 1,5)
          <input
            type="number"
            min="1"
            max="5"
            step="0.1"
            value={cfg.credit_factor ?? 1.5}
            onChange={(e) => setCfg({ ...cfg, credit_factor: e.target.value })}
            data-testid="admin-lmx-credit-factor"
          />
        </label>
        <button className="admin-btn primary" disabled={busy} onClick={publish}
                data-testid="admin-lmx-publish">
          {busy ? <Loader2 className="spin" size={13} /> : <Save size={13} />} Publicar
        </button>
      </div>

      <h3>Consumo</h3>
      <table className="admin-table" data-testid="admin-lmx-usage">
        <thead>
          <tr>
            <th>Usuario</th><th>LMX</th><th>Pedidos</th>
            <th>Tokens entrada</th><th>Tokens salida</th>
          </tr>
        </thead>
        <tbody>
          {usage.length === 0 && (
            <tr><td colSpan={5}>Todavía no hay uso registrado.</td></tr>
          )}
          {usage.map((u) => (
            <tr key={u.user_id}>
              <td><code>{u.user_id}</code></td>
              <td>{u.lmx_enabled ? 'activo' : 'apagado'}</td>
              <td>{u.turns_total}</td>
              <td>{u.in_tokens}</td>
              <td>{u.out_tokens}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
